// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { Link } from 'react-router-dom';

import { Moves, Hangtags } from '@gql/base-types';
import { isMoveLite } from '@utils/tiers';

import { useCustomerConfig } from '@store';
import { checkIfMoveCanBeAssignedOrUnassigned, findOffersInScenarios, formatMoveId } from '@services/moveService';
import { isOfferAccepted, resolveOfferAuditFields } from '@hopdrive/feature-driver-assignment';
import { areMoveTilePropsEqual } from '../utils/moveTileRenderGuard';

import { css } from '@emotion/css';
import { Avatar, Button, Chip, CircularProgress, Grid, Paper, Theme, Tooltip, Typography, useTheme } from '@mui/material';
import { BiPlus, BiSolidCheckCircle, BiSolidLogInCircle, BiSolidUserCheck, BiSolidUserX } from 'react-icons/bi';

import { useTools } from '@hooks/useTools';

import InfoItem from '@components/InfoItem';
import MoveStatusTracker from '@components/MoveStatusTracker';
import MoveTileHangtag from '@components/MoveTileHangtag';
import MoveTypeBadge from '@components/Badges/MoveTypeBadge';
import MoveChatBadge from '@features/moves/components/MoveChatBadge';
import Spacer from '@components/Utils/Spacer';
import { movePath } from '@features/products/productRoutes';

dayjs.extend(relativeTime);

// TYPES ---------------------------------------------------------------------- //

interface MovesDashboardTileProps {
  move?: Moves;
  isAssigning?: boolean;
  handleAssignOrUnassignModalOpen?: (input: any) => void;
}

// COMPONENT ------------------------------------------------------------------ //

const MovesDashboardTile = ({ move, isAssigning, handleAssignOrUnassignModalOpen }: MovesDashboardTileProps) => {
  const theme = useTheme();
  const cls = useStyles(theme, { isConcierge: move?.consumer_type === 'customer' || move?.consumer_type === 'loaner' });
  const clsx = useSxStyles(theme);

  const customerConfig = useCustomerConfig();
  const refLabel = customerConfig?.reference_num_label || 'Reference ID';

  const moveId: number | undefined = move?.id ?? undefined;
  const offers = findOffersInScenarios(move?.scenariotomoves || []);

  const { getFormattedVehicleFromMove, getCancelHeaderFromMove, getCancelReasonFromMove, getCleansedPhoneNumber } =
    useTools();

  const assignOrUnassignDriver = () => {
    if (handleAssignOrUnassignModalOpen) handleAssignOrUnassignModalOpen({ move, offers });
  };

  return (
    <Paper variant='custom' sx={clsx.containerMain}>
      <Link to={movePath(moveId)} className={cls.move}>
        <Grid container spacing={1} alignItems='flex-start' wrap='nowrap' sx={clsx.top}>
          <Grid>
            {move?.consumer_type === 'customer' || move?.consumer_type === 'loaner' ? (
              <Tooltip title='Consumer Name & Phone' placement='top'>
                <div className={cls.consumer}>
                  <Typography sx={clsx.consumerNameText}>{move?.consumer_name}</Typography>
                  <Typography sx={clsx.consumerPhoneText}>{getCleansedPhoneNumber(move?.consumer_phone)}</Typography>
                </div>
              </Tooltip>
            ) : null}

            <Tooltip title='Vehicle Info' placement='top'>
              <Typography sx={clsx.vehicleText}>{getFormattedVehicleFromMove(move)}</Typography>
            </Tooltip>

            <Tooltip title='Lane Info' placement='top'>
              <Typography sx={clsx.laneText}>{move?.lane?.description}</Typography>
            </Tooltip>
          </Grid>

          <Grid size='grow' />

          {isMoveLite(move) && move?.lane?.duration_sec ? (
            <Grid>
              <Tooltip title='Move Duration' placement='top'>
                <Chip
                  label={`${Math.round(dayjs.duration(move?.lane?.duration_sec ?? 0, 'seconds').asMinutes())} min`}
                  variant='outlined'
                />
              </Tooltip>
            </Grid>
          ) : null}

          <Grid>
            <Tooltip title='Move Type' placement='top'>
              <MoveTypeBadge move={move} />
            </Tooltip>
          </Grid>

          {/* SMS Chat Badge — only for a self-managed (lite) move */}
          {move?.consumer_pickup && isMoveLite(move) ? (
            <Grid sx={{ position: 'relative', zIndex: 2 }}>
              <MoveChatBadge moveId={move?.id} consumerPickup={move?.consumer_pickup} variant='icon' size='small' />
            </Grid>
          ) : null}
        </Grid>

        <Spacer size={1} />

        <MoveStatusTracker move={move} size='sm' />

        <Spacer size={1} />

        <Grid container spacing={2} alignItems='flex-start' wrap='nowrap' sx={clsx.bottom}>
          <Grid size='grow'>
            <InfoItem
              size={`sm`}
              keyText={`HopDrive ID`}
              valueText={formatMoveId(move?.id ?? 0, { isLite: isMoveLite(move) })}
            />

            <InfoItem size={`sm`} keyText={refLabel} valueText={move?.reference_num || `N/A`} />

            <InfoItem size={`sm`} keyText={`VIN`} valueText={move?.vehicle_vin || `N/A`} />

            <InfoItem size={`sm`} keyText={`Stock`} valueText={move?.vehicle_stock || `N/A`} />
          </Grid>

          <Grid size='grow'>
            <InfoItem
              size={`sm`}
              keyText={
                move?.consumer_type === `customer` && move?.consumer_at_pickup === 1 ? `Appointment` : `Ready By`
              }
              valueText={dayjs(move?.ready_by).format('M/D h:mm A z')}
            />

            <InfoItem
              size={`sm`}
              keyText={`Created By`}
              valueText={
                move?.createdBy
                  ? move?.createdBy
                  : move?.eventlogs && move?.eventlogs.length > 0
                    ? move?.eventlogs[0].user
                    : 'Unknown'
              }
            />

            <InfoItem size={`sm`} keyText={`Workflow`} valueText={move?.workflowset?.name || 'Unknown'} />
          </Grid>
        </Grid>

        {!move?.cancel_status && move?.status === 'delivery successful' ? <div className={cls.completed} /> : null}

        {move?.cancel_status ? (
          <div className={cls.canceled}>
            <div className={cls.canceledTitle}>
              <Typography sx={clsx.cancelText}>{getCancelHeaderFromMove(move)}</Typography>
            </div>

            <div className={cls.canceledSubtitle}>
              <Typography sx={clsx.cancelReasonText}>{getCancelReasonFromMove(move)}</Typography>
            </div>
          </div>
        ) : null}

        {move?.hangtags && move?.hangtags.length && move?.hangtags[0].type === `yard` ? (
          <MoveTileHangtag hangtag={move?.hangtags[0] as Hangtags} />
        ) : null}
      </Link>

      {isMoveLite(move) ? (
        <div className={cls.containerActions}>
          {(() => {
            // The verdict offer: accepted-first, same rule as resolveDriverAssignment —
            // in a broadcast the newest offer can still be pending while an older one
            // was taken, and the pill must describe the taken one. A force-assigned
            // lite move (driver on the row, no offer) also reads committed, matching
            // the details panel and the scenario-dashboard pill.
            const offer = offers?.find(isOfferAccepted) ?? offers?.[0];
            const isAccepted = isOfferAccepted(offer) || (!offer && !!move?.driver_id);
            const isOffered = offer?.status === 'offered';
            const driverName = move?.driver?.user?.display_name || offer?.driver?.user?.display_name;
            const driverAvatar = move?.driver?.user?.avatar_url || offer?.driver?.user?.avatar_url;
            const driverInitials = driverName?.split(' ').map((n: string) => n[0]).join('').slice(0, 2).toUpperCase();

            // Audit timestamps through the ONE shared fallback implementation.
            const { offeredAt: offeredTime, acceptedAt: acceptedTime } = resolveOfferAuditFields(offer);

            // Format time display
            const getTimeDisplay = () => {
              if (isAccepted && acceptedTime) {
                return dayjs(acceptedTime).format('M/D');
              }
              if (isOffered && offeredTime) {
                // fromNow(true) drops the suffix, so add it here: "Offered 5 minutes ago",
                // the same phrasing as the scenario dashboard's driver pill.
                return `${dayjs(offeredTime).fromNow(true)} ago`;
              }
              return null;
            };

            if (isAssigning) {
              // WORKING state
              return (
                <div className={cls.driverPill}>
                  <div className={cls.workingAvatar}>
                    <CircularProgress size={20} thickness={4} sx={{ color: theme?.palette?.grey?.[400] }} />
                  </div>
                  <Typography sx={clsx.driverPillText}>Working on it...</Typography>
                </div>
              );
            }

            if (isAccepted && driverName) {
              // ACCEPTED state
              return (
                <div className={cls.driverPill}>
                  <Avatar
                    src={driverAvatar || ''}
                    alt={driverName}
                    sx={{ width: 36, height: 36, bgcolor: theme?.palette?.grey?.[400], fontSize: 14, fontWeight: 600 }}
                  >
                    {driverInitials}
                  </Avatar>
                  <div className={cls.driverPillInfo}>
                    <Typography sx={clsx.driverPillName}>{driverName}</Typography>
                    <div className={cls.driverPillStatus}>
                      <BiSolidCheckCircle color={theme?.palette?.success?.main} size={14} />
                      <Typography sx={{ ...clsx.driverPillStatusText, color: theme?.palette?.success?.main }}>
                        Accepted {getTimeDisplay()}
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            }

            if (isOffered && driverName) {
              // OFFERED state
              return (
                <div className={cls.driverPill}>
                  <Avatar
                    src={driverAvatar || ''}
                    alt={driverName}
                    sx={{ width: 36, height: 36, bgcolor: theme?.palette?.grey?.[400], fontSize: 14, fontWeight: 600 }}
                  >
                    {driverInitials}
                  </Avatar>
                  <div className={cls.driverPillInfo}>
                    <Typography sx={clsx.driverPillName}>{driverName}</Typography>
                    <div className={cls.driverPillStatus}>
                      <BiSolidLogInCircle color={theme?.palette?.info?.main} size={14} />
                      <Typography sx={{ ...clsx.driverPillStatusText, color: theme?.palette?.info?.main }}>
                        Offered {getTimeDisplay()}
                      </Typography>
                    </div>
                  </div>
                </div>
              );
            }

            // UNASSIGNED state
            return (
              <div className={cls.driverPill}>
                <div className={cls.unassignedAvatar}>
                  <BiPlus size={16} color={theme?.palette?.grey?.[500]} />
                </div>
                <Typography sx={clsx.driverPillTextMuted}>Not Assigned</Typography>
              </div>
            );
          })()}

          <div className={cls.containerActionsFlex} />

          <Tooltip placement='top' title={`Assign or unassign a driver to complete this move`}>
            <Button
              loading={isAssigning}
              disabled={!checkIfMoveCanBeAssignedOrUnassigned(move) || isAssigning}
              startIcon={move?.driver_id || offers?.length ? <BiSolidUserX /> : <BiSolidUserCheck />}
              size='small'
              variant='contained'
              color={move?.driver_id || offers?.length ? `error` : move?.consumer_type ? `secondary` : `primary`}
              onClick={() => assignOrUnassignDriver()}
            >
              {move?.driver_id || offers?.length ? `Unassign Driver` : `Assign Driver`}
            </Button>
          </Tooltip>
        </div>
      ) : null}
    </Paper>
  );
};

// STYLES --------------------------------------------------------------------- //

const useStyles = (theme?: Theme, options?: { isConcierge?: boolean }) => ({
  container: css`
    position: relative;
  `,

  move: css`
    display: block;
    position: relative;
    padding: 12px;
    border-radius: 8px;
    cursor: pointer;
    text-decoration: none;
    color: inherit;
    user-select: none;
    transition: background-color 0.15s;
    &:hover {
      outline: 2px solid ${options?.isConcierge ? theme?.palette?.secondary?.main : theme?.palette?.primary?.main};
      outline-offset: -2px;
      background-color: ${options?.isConcierge
        ? `${theme?.palette?.secondary?.main}24`
        : `${theme?.palette?.primary?.main}24`};
    }
  `,

  consumer: css`
    margin-bottom: 2px;
  `,

  completed: css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 8px;
    background-color: ${theme?.palette?.background?.paper};
    opacity: 0.5;
  `,

  canceled: css`
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 8px;
    background-color: ${theme?.palette?.utility?.darker}b4;
    ${theme?.breakpoints?.down('md')} {
      background-color: ${theme?.palette?.utility?.darker}c8;
    }
  `,
  canceledTitle: css`
    position: absolute;
    top: ${options?.isConcierge ? '13.75%' : '9%'};
    left: 0;
    right: 0;
  `,
  canceledSubtitle: css`
    position: absolute;
    top: ${options?.isConcierge ? '42.25%' : '34.5%'};
    left: 0;
    right: 0;
  `,

  containerActions: css`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px;
    border-top: 1px solid ${theme?.palette?.divider};
    border-radius: 0 0 10px 10px;
  `,
  containerActionsFlex: css`
    flex: 1;
  `,
  driverPill: css`
    display: flex;
    align-items: center;
    gap: 10px;
  `,
  driverPillInfo: css`
    display: flex;
    flex-direction: column;
    gap: 1px;
  `,
  driverPillStatus: css`
    display: flex;
    align-items: center;
    gap: 4px;
  `,
  unassignedAvatar: css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px dashed ${theme?.palette?.grey?.[400]};
    display: flex;
    align-items: center;
    justify-content: center;
  `,
  workingAvatar: css`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
  `,
});

const useSxStyles = (theme?: Theme) => ({
  containerMain: {
    position: 'relative',
  },

  top: {
    minWidth: '100%',
    marginBottom: theme?.spacing(0.5),
  },
  bottom: {
    minWidth: '100%',
    marginTop: theme?.spacing(0.5),
  },

  consumerNameText: {
    display: 'inline-block',
    marginRight: '4px',
    lineHeight: 1,
    fontSize: '16px',
    fontWeight: 700,
    color: theme?.palette?.secondary?.main,
  },
  consumerPhoneText: {
    display: 'inline-block',
    lineHeight: 1,
    fontSize: '16px',
    fontWeight: 500,
    color: theme?.palette?.secondary?.main,
  },

  vehicleText: {
    marginBottom: '2px',
    lineHeight: 1,
    fontSize: '16px',
    fontWeight: 700,
  },

  laneText: {
    lineHeight: 1,
    fontSize: '12px',
  },

  cancelText: {
    lineHeight: 1,
    textAlign: 'center',
    fontSize: '24px',
    fontWeight: 900,
    color: theme?.palette?.text?.contrastPrimary,
  },
  cancelReasonText: {
    lineHeight: 1,
    textAlign: 'center',
    fontSize: '20px',
    fontWeight: 500,
    color: theme?.palette?.text?.contrastPrimary,
  },

  driverPillName: {
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: 1.2,
  },
  driverPillText: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme?.palette?.text?.secondary,
  },
  driverPillTextMuted: {
    fontSize: '14px',
    fontWeight: 500,
    color: theme?.palette?.text?.disabled,
  },
  driverPillStatusText: {
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: 1.2,
  },
});

// EXPORT -------------------------------------------------------------------- //

// Memoize the component to prevent unnecessary re-renders. The comparator lives
// in moveTileRenderGuard (unit-tested): it keys on the move scalars PLUS an
// offer signal, because offer writes don't bump the moves row's updatedat.
export default React.memo(MovesDashboardTile, areMoveTilePropsEqual);
